import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/app/store/auth.store';
import { toast } from '@/components/feedback/useToast';
import { useCountdown } from '@/shared/hooks/use-countdown';

import { MyProfileApiError } from '../api/my-profile.types';
import {
  useSavePhoneMutation,
  useSendPhoneCodeMutation,
} from '../hooks/use-my-profile-mutations';
import { digitsOnly, isValidPhoneDigits } from '../utils/phone';
import { toPhoneInputValue } from '@/shared/utils/phone';

interface PhoneChangeFormProps {
  // Digits-only form of the current stored phone (e.g. "01011112222"). Used to
  // detect "same as current" without depending on display formatting.
  currentPhoneDigits: string;
  // Fired after a successful save so the parent can collapse the edit area
  // (PRD §3.3.1 step 6 "automatically collapses the edit area").
  onSaved: () => void;
}

function formatTimer(totalSeconds: number): string {
  const safe = Math.max(0, totalSeconds);
  const m = String(Math.floor(safe / 60)).padStart(2, '0');
  const s = String(safe % 60).padStart(2, '0');
  return `${m}:${s}`;
}

// PRD 20 §3.3 — two buttons, mapped to the backend's 2 endpoints:
//   [인증번호 받기] → POST request-otp (send SMS)
//   [전화번호 저장] → POST verify (server checks the code AND commits the new number)
// SHCT-328: the old separate [인증하기] was a CLIENT-ONLY 6-digit format check that
// falsely showed "✓ 인증되었습니다" for any code (the server never saw it until 저장).
// It is removed — [전화번호 저장] is the single action that validates the OTP server-side
// AND commits, so a wrong code surfaces an inline error instead of a fake ✓.
// State machine (FE-side): idle → codeSent → saved. Re-typing newPhone discards the
// code (PRD §3.3.3). The [저장] button is gated on a client 6-digit format check only.
export function PhoneChangeForm({ currentPhoneDigits, onSaved }: PhoneChangeFormProps) {
  const { t } = useTranslation('my-profile');
  const sendCode = useSendPhoneCodeMutation();
  const savePhone = useSavePhoneMutation();
  const setProfile = useAuthStore((s) => s.setProfile);

  const [newPhone, setNewPhone] = useState('');
  const [newPhoneError, setNewPhoneError] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [codeExpiresAt, setCodeExpiresAt] = useState<string | null>(null);
  const [cooldownExpiresAt, setCooldownExpiresAt] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');

  const codeInputRef = useRef<HTMLInputElement>(null);

  const secondsLeft = useCountdown(codeExpiresAt);
  const cooldownSecondsLeft = useCountdown(cooldownExpiresAt);

  const matchesCurrent = newPhone.length > 0 && newPhone === currentPhoneDigits;
  const formatValid = isValidPhoneDigits(newPhone);
  const inCooldown = cooldownSecondsLeft > 0;
  const canRequestCode =
    formatValid && !matchesCurrent && !inCooldown && !sendCode.isPending;
  const timerExpired = codeSent && secondsLeft <= 0;
  // Client-side 6-digit format gate only — NOT a "verified" claim (SHCT-328). The real
  // OTP validation happens server-side when [전화번호 저장] calls verify (verify-commit).
  const canSave =
    codeSent && !timerExpired && code.length === 6 && !savePhone.isPending;

  // Focus the code input the first time it appears.
  useEffect(() => {
    if (codeSent) codeInputRef.current?.focus();
  }, [codeSent]);

  const resetVerification = () => {
    setCodeSent(false);
    setCodeExpiresAt(null);
    setCode('');
    setCodeError('');
  };

  const handleNewPhoneChange = (raw: string) => {
    // SHCT-695 — this screen already had the right behaviour; it now shares the
    // one definition of it so a new phone field cannot be added without it.
    const next = toPhoneInputValue(raw);
    setNewPhone(next);
    setNewPhoneError('');
    // PRD §3.3.3 — editing the new number after sending discards the code.
    if (codeSent) resetVerification();
  };

  const handleSendCode = () => {
    setNewPhoneError('');
    if (!newPhone) {
      setNewPhoneError(t('phoneChange.errors.empty'));
      return;
    }
    if (!formatValid) {
      setNewPhoneError(t('phoneChange.errors.format'));
      return;
    }
    if (matchesCurrent) {
      setNewPhoneError(t('phoneChange.errors.sameAsCurrent'));
      return;
    }
    sendCode.mutate(
      { newPhone },
      {
        onSuccess: () => {
          setCodeSent(true);
          setCodeExpiresAt(new Date(Date.now() + 5 * 60_000).toISOString());
          setCooldownExpiresAt(new Date(Date.now() + 60_000).toISOString());
          setCode('');
          setCodeError('');
          toast.success(t('phoneChange.toasts.codeSent'));
        },
        onError: (e) => {
          if (e instanceof MyProfileApiError) {
            if (e.code === 'OTP_RATE_LIMITED') {
              // Server is the authority on cooldown remainder (fallback 60s).
              const retry = e.meta?.retryAfterSeconds ?? 60;
              setCooldownExpiresAt(new Date(Date.now() + retry * 1000).toISOString());
              return;
            }
            if (e.code === 'PHONE_INVALID') {
              setNewPhoneError(e.fields?.newPhone ?? t('phoneChange.errors.format'));
              return;
            }
          }
          toast.error(t('phoneChange.toasts.smsFailed'));
        },
      },
    );
  };

  const handleCodeChange = (raw: string) => {
    setCode(digitsOnly(raw).slice(0, 6));
    setCodeError('');
  };

  const sendButtonLabel = inCooldown
    ? t('phoneChange.actions.resendIn', { seconds: cooldownSecondsLeft })
    : sendCode.isPending
      ? t('phoneChange.actions.sending')
      : t('phoneChange.actions.sendCode');

  // [전화번호 저장] — the real verify-commit. The server validates the code and saves the number.
  const handleSave = () => {
    if (!canSave) return;
    savePhone.mutate(
      { newPhone, code },
      {
        onSuccess: (profile) => {
          // Keep the GNB email-dropdown context coherent without waiting on the
          // /my-profile refetch (mutation hook already invalidates the cache).
          setProfile({ phone: profile.phone });
          toast.success(t('phoneChange.toasts.saveSuccess'));
          onSaved();
        },
        onError: (e) => {
          if (e instanceof MyProfileApiError) {
            // A bad/expired/burned code surfaces here (verify-commit). Stay in the
            // code-entry state so the user can re-enter without a full restart.
            if (e.code === 'OTP_INVALID') {
              setCodeError(t('phoneChange.errors.codeMismatch'));
              return;
            }
            if (e.code === 'OTP_EXPIRED' || e.code === 'OTP_RATE_LIMITED') {
              setCodeError(t('phoneChange.errors.codeExpired'));
              return;
            }
            if (e.code === 'PHONE_INVALID') {
              resetVerification();
              setNewPhoneError(t('phoneChange.errors.format'));
              return;
            }
            if (e.code === 'PHONE_DUPLICATE') {
              // Number already used by another admin. Anti-enumeration: show the single shared
              // generic message (same string as invite/signup), locale-aware. Reset verification
              // so they enter a different number and re-verify.
              resetVerification();
              setNewPhoneError(t('errors.phoneRejected', { ns: 'common' }));
              return;
            }
          }
          toast.error(t('phoneChange.toasts.saveFailed'));
        },
      },
    );
  };

  return (
    <div
      style={{
        marginTop: 12,
        padding: 16,
        background: '#fafbff',
        border: '1px solid #dbeafe',
        borderRadius: 6,
      }}
    >
      <div className="form-field" style={{ marginBottom: 16 }}>
        <label className="form-field__label" htmlFor="mpPhoneNew">
          {t('phoneChange.newPhone.label')}
        </label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <input
            id="mpPhoneNew"
            type="tel"
            inputMode="numeric"
            placeholder={t('phoneChange.newPhone.placeholder')}
            className={`form-field__input${newPhoneError ? ' form-field__input--error' : ''}`}
            value={newPhone}
            onChange={(e) => handleNewPhoneChange(e.target.value)}
            style={{ flex: 1 }}
            autoFocus
            aria-invalid={!!newPhoneError}
            aria-describedby={newPhoneError ? 'mpPhoneNewError' : undefined}
          />
          <button
            type="button"
            className="btn btn-default btn-secondary"
            style={{ height: 40, minWidth: 120 }}
            disabled={!canRequestCode}
            onClick={handleSendCode}
          >
            {sendButtonLabel}
          </button>
        </div>
        {newPhoneError && (
          <p id="mpPhoneNewError" className="form-field__error" role="alert">
            {newPhoneError}
          </p>
        )}
        {!newPhoneError && matchesCurrent && (
          <p className="form-field__error" role="alert">
            {t('phoneChange.errors.sameAsCurrent')}
          </p>
        )}
      </div>

      {codeSent && (
        <div className="form-field" style={{ marginBottom: 16 }}>
          <label className="form-field__label" htmlFor="mpPhoneCode">
            {t('phoneChange.code.label')}
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              id="mpPhoneCode"
              ref={codeInputRef}
              type="text"
              inputMode="numeric"
              placeholder={t('phoneChange.code.placeholder')}
              className={`form-field__input${codeError ? ' form-field__input--error' : ''}`}
              value={code}
              onChange={(e) => handleCodeChange(e.target.value)}
              disabled={timerExpired}
              style={{ flex: 1 }}
              aria-invalid={!!codeError}
              aria-describedby={codeError ? 'mpPhoneCodeError' : undefined}
            />
            <span
              aria-live="polite"
              style={{
                color: '#dc2626',
                fontSize: 13,
                minWidth: 60,
                textAlign: 'center',
                fontWeight: 600,
              }}
            >
              {formatTimer(secondsLeft)}
            </span>
          </div>
          {/* SHCT-329 — surface the expiry message automatically when the timer
              hits 00:00 (no button click needed, since the action is disabled). */}
          {timerExpired ? (
            <p id="mpPhoneCodeError" className="form-field__error" role="alert">
              {t('phoneChange.errors.codeExpired')}
            </p>
          ) : codeError ? (
            <p id="mpPhoneCodeError" className="form-field__error" role="alert">
              {codeError}
            </p>
          ) : null}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          className="btn btn-default btn-primary"
          style={{ height: 40 }}
          disabled={!canSave}
          onClick={handleSave}
        >
          {savePhone.isPending
            ? t('phoneChange.actions.saving')
            : t('phoneChange.actions.save')}
        </button>
      </div>
    </div>
  );
}
